import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Flower2,
  Footprints,
  LayoutGrid,
  MonitorPlay,
  Pause,
  Play,
  Sprout,
  X,
} from "lucide-react";
import ShoeArt from "./ShoeArt";
import Pricing from "./Pricing";
import { DASHBOARD_URL, PORTAL_URL } from "./links";
import "./website.css";
const TaskDemo = lazy(() => import("@sprout/ui/wall/TaskDemo3D"));
const steps = ["Find your pair", "Slip them on", "Fasten the straps"];
type Beat = 0 | 1 | 2;
function ChoreDemo({ close }: { close: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [phase, setPhase] = useState<"watch" | "try" | "done">("watch");
  const [step, setStep] = useState<Beat>(0),
    [manual, setManual] = useState<Beat>(),
    [play, setPlay] = useState(true);
  const [reduced, setReduced] = useState(
    () =>
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false,
  );
  useEffect(() => {
    const el = dialog.current;
    el?.showModal();
    const media = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    const changed = () => setReduced(media.matches);
    media?.addEventListener("change", changed);
    return () => {
      el?.close();
      media?.removeEventListener("change", changed);
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="site-demo"
      aria-labelledby="demo-title"
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
    >
      <header>
        <span className="site-demo-kicker">
          <Sprout size={16} /> Sample chore
        </span>
        <button
          type="button"
          className="site-close"
          aria-label="Close sample"
          onClick={close}
        >
          <X size={20} />
        </button>
      </header>
      <h2 id="demo-title">
        {phase === "done" ? "Shoes on. You did it!" : "Shoes on"}
      </h2>
      {phase === "watch" ? (
        <>
          <div className="site-lesson">
            <Suspense fallback={<p>Opening the lesson…</p>}>
              <TaskDemo
                questId="ready"
                avatar="fox"
                reducedMotion={reduced}
                playing={play}
                step={manual}
                onStageChange={setStep}
              />
            </Suspense>
          </div>
          <div className="site-beat">
            <span>
              Step {step + 1} of 3 <b>{steps[step]}</b>
            </span>
            {!reduced && (
              <button
                className="site-close"
                aria-label={play ? "Pause lesson" : "Play lesson"}
                onClick={() => setPlay(!play)}
              >
                {play ? <Pause size={17} /> : <Play size={17} />}
              </button>
            )}
            <button
              className="site-close"
              aria-label="Next step"
              onClick={() => {
                const next = ((step + 1) % 3) as Beat;
                setPlay(false);
                setStep(next);
                setManual(next);
              }}
            >
              <ChevronRight size={20} />
            </button>
          </div>
          <button className="site-btn primary" onClick={() => setPhase("try")}>
            My turn <ArrowRight size={17} />
          </button>
        </>
      ) : phase === "try" ? (
        <>
          <div className="site-practice">
            <ShoeArt />
            <strong>Try putting on your shoes.</strong>
          </div>
          <button className="site-btn primary" onClick={() => setPhase("done")}>
            <Check size={18} /> I’m finished
          </button>
        </>
      ) : (
        <>
          <div className="site-win">
            <Flower2 size={62} />
            <p>A flower for your effort.</p>
          </div>
          <a className="site-btn primary" href={DASHBOARD_URL + "#sign-up"}>
            Create a family <ArrowRight size={16} />
          </a>
          <button className="site-text" onClick={close}>
            Back to website
          </button>
        </>
      )}
    </dialog>
  );
}
export default function Website() {
  const [demo, setDemo] = useState(false);
  return (
    <div className="site">
      <header className="site-header">
        <a className="site-brand" href="/" aria-label="Sprout home">
          <img src="/plant-logo.svg" alt="" />
          sprout<span>.</span>
        </a>
        <nav aria-label="Website">
          <a href={PORTAL_URL}>Wall app</a>
          <a className="site-signin" href={DASHBOARD_URL + "#sign-in"}>
            Parent sign in <ArrowRight size={15} />
          </a>
        </nav>
      </header>
      <main>
        <section className="site-hero">
          <div className="site-intro">
            <div className="site-overline">
              <span /> Family routines
            </div>
            <h1>
              Less reminding.
              <br />
              More <em>“I did it.”</em>
            </h1>
            <p>
              Buddy shows your child how to do a chore. They try it, earn
              rewards, and grow a little more independent.
            </p>
            <div className="site-hero-actions">
              <button
                className="site-btn primary"
                onClick={() => setDemo(true)}
              >
                <Play size={17} fill="currentColor" /> Try a chore
              </button>
              <a className="site-btn secondary" href={DASHBOARD_URL + "#sign-up"}>
                Create a family <ArrowRight size={16} />
              </a>
            </div>
            <small>Free for families. No camera needed to try.</small>
          </div>
          <div className="site-postcard">
            <div className="site-card-surface">
              <div className="site-postcard-top">
                <span>
                  <img src="/fox.svg" alt="" />
                  Buddy Garden
                </span>
                <span className="site-sample">SAMPLE</span>
              </div>
              <ShoeArt />
              <div className="site-poster-bottom">
                <div>
                  <small>MORNING ROUTINE</small>
                  <h2>Shoes on</h2>
                </div>
                <Footprints size={27} />
              </div>
            </div>
          </div>
        </section>
        <section className="site-doors" aria-label="Open an app">
          <a className="site-door parent-door" href={DASHBOARD_URL}>
            <span className="site-door-icon">
              <LayoutGrid />
            </span>
            <div>
              <h2>Parent space</h2>
              <p>Plan challenges, review effort, and see progress.</p>
            </div>
            <ArrowRight size={19} />
          </a>
          <a className="site-door wall-door" href={PORTAL_URL}>
            <span className="site-door-icon">
              <MonitorPlay />
            </span>
            <div>
              <h2>Wall app</h2>
              <p>Pair a shared screen and open Buddy Garden.</p>
            </div>
            <ArrowRight size={19} />
          </a>
        </section>
        <Pricing />
      </main>
      {demo && <ChoreDemo close={() => setDemo(false)} />}
    </div>
  );
}
